// Workflow-Editor in einer Spalte: Name und Status oben, darunter Auslöser, Bedingungen, Schritte (aufklappbare Zeilen)
// und Einstellungen; ganz unten die letzten Läufe. Speichern mit ⌘S. Aktiviert wird erst, wenn alles vollständig ist.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fehlerText } from "../api";
import { workflowsApi, type Ausloeser, type Bedingung, type Schritt, type SchrittArt, type Workflow, type WorkflowEingabe, type WorkflowFilter, type WorkflowStatus } from "../api/workflows";
import { useAuth } from "../auth/AuthContext";
import { Laden } from "../components/Bausteine";
import { FehlerBox } from "../components/Fehler";
import { Icon } from "../components/Icon";
import { MenueKnopf, type MenuePunkt } from "../components/Menue";
import { useMeldung } from "../components/Meldungen";
import { Auswahl } from "../components/Auswahl";
import { useMeta } from "../daten/meta";
import { useSeitenKopf } from "../daten/seite";
import { kopieren, modTaste, useTasten } from "../werkzeuge/tasten";
import { AusloeserEditor } from "./AusloeserEditor";
import { Bedingungen } from "./Bedingungen";
import { useLaden } from "./daten";
import { ausloeserProblem, leererAusloeser, neuerSchritt, schrittInfo, schrittProblem, SCHRITTE, WORKFLOW_STATUS } from "./hilfen";
import { LaeufeTabelle, type LaufAktion } from "./LaeufeTabelle";
import { SchrittZeile } from "./SchrittZeile";
import { SendefensterEditor } from "./SendefensterEditor";
import { FENSTER_STANDARD, zielProblem } from "./teil2Hilfen";
import type { Sendefenster, Ziel } from "./teil2Api";
import { ZieleEditor } from "./ZieleEditor";
import "./workflows2.css";

interface Stand {
  name: string;
  beschreibung: string;
  ausloeser: Ausloeser;
  filter: WorkflowFilter;
  schritte: Schritt[];
  empfaengerModus: "einmal" | "mehrfach";
  absender: string | null;
  /** Runde 10: Ziele, Sendefenster und Blackout Dates */
  ziele: Ziel[];
  sendefenster: Sendefenster;
  blackoutBeachten: boolean;
  /** übrige Einstellungen, die der Editor nicht kennt (z. B. Trockenmodus), gehen unverändert zurück */
  rest: Record<string, unknown>;
}

const EIGENE_EINSTELLUNGEN = ["empfaenger_modus", "absender_user_id", "sendefenster", "blackout_beachten", "ziele"];

const NEU: Stand = {
  name: "Neuer Workflow",
  beschreibung: "",
  ausloeser: leererAusloeser("manuell"),
  filter: { bedingungen: [] },
  schritte: [],
  empfaengerModus: "einmal",
  absender: null,
  ziele: [],
  sendefenster: FENSTER_STANDARD,
  blackoutBeachten: true,
  rest: {},
};

function standAus(w: Workflow): Stand {
  return {
    name: w.name,
    beschreibung: w.beschreibung ?? "",
    ausloeser: w.ausloeser?.art ? w.ausloeser : leererAusloeser("manuell"),
    filter: { bedingungen: w.filter?.bedingungen ?? [] },
    schritte: w.schritte,
    empfaengerModus: w.einstellungen?.empfaenger_modus ?? "einmal",
    absender: (w.einstellungen?.absender_user_id as string | null | undefined) ?? null,
    ziele: (w.einstellungen?.ziele as Ziel[] | undefined) ?? [],
    sendefenster: { ...FENSTER_STANDARD, ...((w.einstellungen?.sendefenster as Sendefenster | undefined) ?? {}) },
    blackoutBeachten: w.einstellungen?.blackout_beachten !== false,
    rest: Object.fromEntries(Object.entries(w.einstellungen ?? {}).filter(([k]) => !EIGENE_EINSTELLUNGEN.includes(k))),
  };
}

function eingabeAus(s: Stand): WorkflowEingabe {
  return {
    name: s.name.trim(),
    beschreibung: s.beschreibung.trim() || null,
    ausloeser: s.ausloeser,
    filter: s.filter as unknown as Record<string, unknown>,
    schritte: s.schritte,
    einstellungen: {
      ...s.rest,
      empfaenger_modus: s.empfaengerModus,
      absender_user_id: s.absender,
      sendefenster: s.sendefenster,
      blackout_beachten: s.blackoutBeachten,
      ziele: s.ziele,
    },
  };
}

function Abschnitt({ titel, hinweis, children, rechts }: { titel: string; hinweis?: string; children: React.ReactNode; rechts?: React.ReactNode }) {
  return (
    <section className="wf-abschnitt">
      <div className="wf-abschnitt__kopf">
        <h2>{titel}</h2>
        {hinweis ? <span className="leise wf-abschnitt__hinweis">{hinweis}</span> : null}
        {rechts ? <span className="wf-abschnitt__rechts">{rechts}</span> : null}
      </div>
      {children}
    </section>
  );
}

export default function WorkflowEditor() {
  const { workflowId } = useParams();
  const neu = !workflowId;
  const navigate = useNavigate();
  const meldung = useMeldung();
  const meta = useMeta();
  const { nutzer } = useAuth();
  const kannVerwalten = !!nutzer?.permissions.includes("workflows_verwalten");

  const [stand, setStand] = useState<Stand>(NEU);
  const [server, setServer] = useState<Workflow | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [laedt, setLaedt] = useState(!neu);
  const [laeuft, setLaeuft] = useState(false);
  const [offen, setOffen] = useState<Set<string>>(new Set());

  useSeitenKopf([{ label: "Workflows", to: "/workflows" }, { label: neu ? "Neuer Workflow" : (server?.name ?? "…") }]);

  const laden = async () => {
    if (!workflowId) return;
    try {
      const r = await workflowsApi.eins(workflowId);
      setServer(r.workflow);
      setStand(standAus(r.workflow));
      setFehler(null);
    } catch (e) {
      setFehler(e);
    } finally {
      setLaedt(false);
    }
  };

  useEffect(() => {
    setServer(null);
    setFehler(null);
    setOffen(new Set());
    if (neu) {
      setStand({ ...NEU, ausloeser: leererAusloeser("manuell") });
      setLaedt(false);
    } else {
      setLaedt(true);
      void laden();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId]);

  const archiviert = server?.status === "archiviert";
  const nurLesen = !kannVerwalten || archiviert;
  const bezug = useMemo(() => JSON.stringify(neu ? NEU : server ? standAus(server) : null), [neu, server]);
  const geaendert = neu ? true : server !== null && JSON.stringify(stand) !== bezug;

  const probleme = useMemo(() => {
    const liste: string[] = [];
    const a = ausloeserProblem(stand.ausloeser);
    if (a) liste.push(`Auslöser: ${a}`);
    if (!stand.schritte.length) liste.push("Der Workflow hat noch keinen Schritt.");
    stand.schritte.forEach((s, i) => {
      const p = schrittProblem(s);
      if (p) liste.push(`Schritt ${i + 1} (${s.name || schrittInfo(s.art).label}): ${p}`);
    });
    stand.ziele.forEach((z, i) => {
      const p = zielProblem(z);
      if (p) liste.push(`Ziel ${i + 1}: ${p}`);
    });
    return liste;
  }, [stand]);

  const setzen = (teil: Partial<Stand>) => setStand((s) => ({ ...s, ...teil }));
  const schrittAendern = (id: string, s: Schritt) => setzen({ schritte: stand.schritte.map((x) => (x.id === id ? s : x)) });

  const speichern = async (): Promise<Workflow | null> => {
    if (!stand.name.trim()) {
      meldung.zeige("fehler", "Der Workflow braucht einen Namen.");
      return null;
    }
    setLaeuft(true);
    try {
      if (neu) {
        const r = await workflowsApi.anlegen(eingabeAus(stand));
        meldung.ok("Workflow angelegt.");
        navigate(`/workflows/${encodeURIComponent(r.workflow.id)}`, { replace: true });
        return r.workflow;
      }
      const r = await workflowsApi.aendern(workflowId!, eingabeAus(stand));
      setServer(r.workflow);
      setStand(standAus(r.workflow));
      meldung.ok("Gespeichert.");
      return r.workflow;
    } catch (e) {
      meldung.fehler(e, "Nicht gespeichert");
      return null;
    } finally {
      setLaeuft(false);
    }
  };

  const statusAendern = async (status: WorkflowStatus) => {
    if (!server) return;
    setLaeuft(true);
    try {
      if (geaendert && !archiviert) {
        const gespeichert = await workflowsApi.aendern(server.id, eingabeAus(stand));
        setServer(gespeichert.workflow);
      }
      const r = await workflowsApi.statusSetzen(server.id, status);
      setServer(r.workflow);
      setStand(standAus(r.workflow));
      meldung.ok(status === "aktiv" ? "Workflow ist aktiv." : status === "pausiert" ? "Workflow pausiert." : status === "archiviert" ? "Workflow archiviert, laufende Läufe sind pausiert." : "Workflow ist wieder ein Entwurf.");
    } catch (e) {
      meldung.fehler(e, "Status nicht geändert");
    } finally {
      setLaeuft(false);
    }
  };

  const duplizieren = async () => {
    if (!server) return;
    try {
      const r = await workflowsApi.duplizieren(server.id);
      meldung.ok("Kopie als Entwurf angelegt.");
      navigate(`/workflows/${encodeURIComponent(r.workflow.id)}`);
    } catch (e) {
      meldung.fehler(e, "Nicht dupliziert");
    }
  };

  useTasten({
    "mod+s": () => {
      if (!nurLesen && geaendert && !laeuft) void speichern();
    },
  });

  const verschieben = (i: number, richtung: -1 | 1) => {
    const liste = [...stand.schritte];
    const j = i + richtung;
    if (j < 0 || j >= liste.length) return;
    [liste[i], liste[j]] = [liste[j], liste[i]];
    setzen({ schritte: liste });
  };
  const schrittDuplizieren = (i: number) => {
    const quelle = stand.schritte[i];
    const kopie = { ...neuerSchritt(quelle.art, stand.schritte), name: quelle.name, config: JSON.parse(JSON.stringify(quelle.config)) as Record<string, unknown> };
    const liste = [...stand.schritte];
    liste.splice(i + 1, 0, kopie);
    setzen({ schritte: liste });
    setOffen((o) => new Set(o).add(kopie.id));
  };
  const schrittHinzufuegen = (art: SchrittArt) => {
    const s = neuerSchritt(art, stand.schritte);
    setzen({ schritte: [...stand.schritte, s] });
    setOffen((o) => new Set(o).add(s.id));
    window.setTimeout(() => document.querySelector(`[data-schritt="${s.id}"]`)?.scrollIntoView({ block: "nearest", behavior: "smooth" }), 50);
  };

  const hinzufuegenPunkte: MenuePunkt[] = SCHRITTE.map((s) => ({ label: s.label, icon: s.icon, hinweis: s.text, rechts: <span className="leise wf-menue-text">{s.text}</span>, onClick: () => schrittHinzufuegen(s.art) }));

  const menuePunkte: MenuePunkt[] = [
    { label: "Duplizieren", icon: "kopieren", deaktiviert: neu || !kannVerwalten, onClick: () => void duplizieren() },
    { label: "Link kopieren", icon: "link", deaktiviert: neu, onClick: () => void kopieren(`${window.location.origin}/workflows/${workflowId}`).then((ok) => (ok ? meldung.ok("Link kopiert.") : meldung.zeige("fehler", "Kopieren hat nicht geklappt."))) },
    { label: "Läufe ansehen", icon: "aktivitaet", deaktiviert: neu, onClick: () => navigate(`/workflows/laeufe?workflow_id=${encodeURIComponent(workflowId ?? "")}`) },
    { label: "Bericht ansehen", icon: "report", deaktiviert: neu, onClick: () => navigate(`/workflows/${encodeURIComponent(workflowId ?? "")}/bericht`) },
    ...(server && kannVerwalten
      ? archiviert
        ? [{ label: "Aus dem Archiv holen", icon: "neu-laden" as const, trenner: true, onClick: () => void statusAendern("entwurf") }]
        : [{ label: "Archivieren", icon: "schloss" as const, trenner: true, onClick: () => void statusAendern("archiviert") }]
      : []),
  ];

  if (laedt) return <Laden />;
  if (fehler && !server && !neu) return <div className="wf-seite"><FehlerBox fehler={fehler} onNochmal={() => void laden()} /></div>;

  const status = server?.status ?? "entwurf";
  const k = server?.kennzahlen;

  return (
    <div className="wf-seite">
      <div className="wf-kopf">
        <Link to="/workflows" className="wf-kopf__zurueck" title="Zu allen Workflows">
          <Icon name="chevron-links" groesse={14} />
          Workflows
        </Link>
        <input
          className="wf-kopf__name"
          value={stand.name}
          readOnly={nurLesen}
          aria-label="Name des Workflows"
          placeholder="Name des Workflows"
          onChange={(e) => setzen({ name: e.target.value })}
        />
        {!neu ? <span className={`wf-status wf-status--${status}`}>{WORKFLOW_STATUS[status]}</span> : <span className="wf-status wf-status--entwurf">Neu</span>}
        {k ? (
          <span className="wf-kopf__zahlen leise" title="Läufe dieses Workflows">
            {k.aktiv} aktiv · {k.fertig} fertig{k.fehler ? <span className="text-gefahr"> · {k.fehler} Fehler</span> : null}
          </span>
        ) : null}
        <span className="wf-kopf__rechts">
          {geaendert && !nurLesen ? <span className="leise wf-kopf__ungespeichert">{neu ? "" : "Nicht gespeichert"}</span> : null}
          {!nurLesen ? (
            <button type="button" className="knopf" disabled={laeuft || !geaendert} onClick={() => void speichern()} title={`Speichern (${modTaste}S)`}>
              {neu ? "Anlegen" : "Speichern"}
            </button>
          ) : null}
          {!neu && kannVerwalten && !archiviert ? (
            status === "aktiv" ? (
              <button type="button" className="knopf" disabled={laeuft} onClick={() => void statusAendern("pausiert")}>
                Pausieren
              </button>
            ) : (
              <button type="button" className="knopf knopf--primaer" disabled={laeuft || probleme.length > 0} title={probleme.length ? "Erst ergänzen, was fehlt" : "Aktivieren"} onClick={() => void statusAendern("aktiv")}>
                {status === "pausiert" ? "Fortsetzen" : "Aktivieren"}
              </button>
            )
          ) : null}
          <MenueKnopf knopfKlasse="symbolknopf symbolknopf--rand" titel="Mehr" punkte={menuePunkte} ausrichtung="end" breite={220}>
            <Icon name="mehr" groesse={16} />
          </MenueKnopf>
        </span>
      </div>

      {archiviert ? (
        <div className="hinweis wf-meldung">
          <Icon name="info" groesse={15} />
          <span>Dieser Workflow ist archiviert und lässt sich nur ansehen. Laufende Läufe sind pausiert.</span>
        </div>
      ) : !kannVerwalten ? (
        <div className="hinweis wf-meldung">
          <Icon name="info" groesse={15} />
          <span>Workflows bearbeiten dürfen Admins und Super User. Du kannst ihn ansehen.</span>
        </div>
      ) : null}

      {probleme.length > 0 && !nurLesen ? (
        <div className="wf-meldung wf-meldung--warnung" role="status">
          <Icon name="warnung" groesse={15} />
          <div>
            <strong>{status === "aktiv" ? "Ergänzen, bevor du speicherst:" : "Noch nicht aktivierbar:"}</strong>
            <ul>
              {probleme.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      <fieldset disabled={nurLesen} className="wf-fieldset">
        <Abschnitt titel="Beschreibung" hinweis="optional">
          <textarea className="eingabe eingabe--text wf-beschreibung" rows={2} value={stand.beschreibung} placeholder="Wofür ist der Ablauf da?" onChange={(e) => setzen({ beschreibung: e.target.value })} />
        </Abschnitt>

        <Abschnitt titel="Auslöser">
          <AusloeserEditor ausloeser={stand.ausloeser} onChange={(a) => setzen({ ausloeser: a })} />
        </Abschnitt>

        <Abschnitt titel="Bedingungen" hinweis="optional, gilt beim Einschreiben">
          <Bedingungen bedingungen={stand.filter.bedingungen as Bedingung[]} onChange={(b) => setzen({ filter: { bedingungen: b } })} leerText="Jeder Lead darf in den Workflow." />
        </Abschnitt>

        <Abschnitt titel="Schritte" hinweis="laufen von oben nach unten">
          {stand.schritte.length === 0 ? <p className="leise wf-leer">Noch kein Schritt. Füge unten den ersten hinzu.</p> : null}
          <ol className="wf-schritte">
            {stand.schritte.map((s, i) => (
              <SchrittZeile
                key={s.id}
                nummer={i + 1}
                schritt={s}
                offen={offen.has(s.id)}
                nurLesen={nurLesen}
                erster={i === 0}
                letzter={i === stand.schritte.length - 1}
                onOffen={(an) => setOffen((o) => { const n = new Set(o); if (an) n.add(s.id); else n.delete(s.id); return n; })}
                onChange={(x) => schrittAendern(s.id, x)}
                onVerschieben={(r) => verschieben(i, r)}
                onDuplizieren={() => schrittDuplizieren(i)}
                onEntfernen={() => setzen({ schritte: stand.schritte.filter((x) => x.id !== s.id) })}
              />
            ))}
          </ol>
          {!nurLesen ? (
            <div className="wf-schritte__neu">
              <MenueKnopf knopfKlasse="knopf" titel="Schritt hinzufügen" punkte={hinzufuegenPunkte} breite={380}>
                <Icon name="plus" groesse={14} />
                Schritt hinzufügen
              </MenueKnopf>
            </div>
          ) : null}
        </Abschnitt>

        <Abschnitt titel="Ziele" hinweis="optional, beenden den Lauf, sobald sie erreicht sind">
          <ZieleEditor ziele={stand.ziele} onChange={(z) => setzen({ ziele: z })} nurLesen={nurLesen} />
        </Abschnitt>

        <Abschnitt titel="Sendefenster" hinweis="wann E-Mail- und SMS-Schritte fällig werden">
          <SendefensterEditor
            fenster={stand.sendefenster}
            blackoutBeachten={stand.blackoutBeachten}
            onFenster={(f) => setzen({ sendefenster: f })}
            onBlackout={(an) => setzen({ blackoutBeachten: an })}
          />
        </Abschnitt>

        <Abschnitt titel="Einstellungen">
          <div className="wf-form">
            <div className="wf-form__zeile">
              <div className="feld wf-feld">
                <span className="feld__label">Einschreiben</span>
                <div className="segment" role="group" aria-label="Empfänger-Modus">
                  {(["einmal", "mehrfach"] as const).map((m) => (
                    <button key={m} type="button" className={`segment__knopf${stand.empfaengerModus === m ? " segment__knopf--an" : ""}`} onClick={() => setzen({ empfaengerModus: m })}>
                      {m === "einmal" ? "Jeder Lead einmal" : "Mehrfach möglich"}
                    </button>
                  ))}
                </div>
                <span className="leise wf-feld__hinweis">{stand.empfaengerModus === "einmal" ? "Ein Lead läuft höchstens einmal durch den Workflow." : "Ein Lead kann wieder einsteigen, sobald sein Lauf zu Ende ist (nicht öfter als alle 10 Minuten)."}</span>
              </div>
              <div className="feld wf-feld">
                <span className="feld__label">Schreibt im Namen von</span>
                <Auswahl
                  optionen={meta.nutzerListe.filter((u) => u.is_active || u.close_user_id === stand.absender || u.id === stand.absender).map((u) => ({ wert: u.close_user_id ?? u.id, label: u.name }))}
                  wert={stand.absender}
                  leerErlaubt
                  platzhalter="Wer startet, sonst der Besitzer"
                  onWahl={(w) => setzen({ absender: (w as string | null) ?? null })}
                />
                <span className="leise wf-feld__hinweis">Aufgaben und Änderungen entstehen mit dem Close-Schlüssel dieser Person. Mails und SMS entstehen nur als Entwurf, gesendet wird nichts von allein.</span>
              </div>
            </div>
          </div>
        </Abschnitt>
      </fieldset>

      {!neu && server ? <LetzteLaeufe workflowId={server.id} kannVerwalten={kannVerwalten} /> : null}
      <p className="leise wf-fuss">{fehler ? fehlerText(fehler) : null}</p>
    </div>
  );
}

function LetzteLaeufe({ workflowId, kannVerwalten }: { workflowId: string; kannVerwalten: boolean }) {
  const meldung = useMeldung();
  const g = useLaden(() => workflowsApi.laeufe({ workflow_id: workflowId, limit: 10 }), [workflowId], 15000);
  const aktion = async (l: { id: string }, a: LaufAktion) => {
    try {
      await workflowsApi.laufAktion(l.id, a);
      await g.neuLaden();
    } catch (e) {
      meldung.fehler(e, "Lauf nicht geändert");
    }
  };
  return (
    <Abschnitt
      titel="Letzte Läufe"
      rechts={
        <>
          <Link className="textknopf" to={`/workflows/${encodeURIComponent(workflowId)}/bericht`}>
            Bericht
          </Link>
          <Link className="textknopf" to={`/workflows/laeufe?workflow_id=${encodeURIComponent(workflowId)}`}>
            Alle Läufe
          </Link>
        </>
      }
    >
      {g.laedt && !g.daten ? (
        <Laden klein />
      ) : g.daten && g.daten.data.length ? (
        <LaeufeTabelle laeufe={g.daten.data} zeigeWorkflow={false} kannVerwalten={kannVerwalten} onAktion={aktion} />
      ) : (
        <p className="leise wf-leer">Noch keine Läufe.</p>
      )}
    </Abschnitt>
  );
}
